import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Lock, Crown, Zap, Star, Gem, ArrowUpRight, Sparkles, Leaf } from 'lucide-react';

const PLAN_VISUALS = {
    free: { icon: Star, color: '#6B7280', bg: 'rgba(107,114,128,0.08)' },
    lite: { icon: Leaf, color: '#10B981', bg: 'rgba(16,185,129,0.08)' },
    start: { icon: Zap, color: '#F59E0B', bg: 'rgba(245,158,11,0.08)' },
    gestao: { icon: Crown, color: '#3B82F6', bg: 'rgba(59,130,246,0.08)' },
    pro: { icon: Gem, color: '#8B5CF6', bg: 'rgba(139,92,246,0.08)' },
    enterprise: { icon: Sparkles, color: 'var(--sv-accent)', bg: 'rgba(var(--sv-accent-rgb), 0.08)' },
};

const PLAN_NAMES = {
    free: 'Free', lite: 'Lite', start: 'Start', gestao: 'Gestao', pro: 'Pro', enterprise: 'Enterprise'
};

export default function UpgradeModal({ isOpen, onClose, feature, upgradePlan, currentPlan, limitInfo }) {
    const navigate = useNavigate();

    if (!isOpen) return null;

    const target = PLAN_VISUALS[upgradePlan] || PLAN_VISUALS.start;
    const TargetIcon = target.icon;
    const targetName = PLAN_NAMES[upgradePlan] || upgradePlan;
    const prices = { free: 0, lite: 9.90, start: 19.90, gestao: 79.90, pro: 149.90, enterprise: 0 };
    const price = prices[upgradePlan] || 0;

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }} onClick={onClose} data-testid="upgrade-modal">
            <div className="relative w-full max-w-md mx-4 rounded-2xl overflow-hidden" style={{ background: 'var(--surface)', border: `1px solid ${target.color}40`, boxShadow: `0 0 60px ${target.color}15` }} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="relative p-6 pb-4" style={{ background: target.bg }}>
                    <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--surface-2)' }} data-testid="upgrade-modal-close">
                        <X size={14} style={{ color: 'var(--muted)' }} />
                    </button>
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${target.color}18`, border: `1px solid ${target.color}30` }}>
                            <Lock size={20} style={{ color: target.color }} />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>
                                Recurso Bloqueado
                            </h3>
                            <p className="text-xs" style={{ color: 'var(--muted)' }}>
                                {feature || 'Este recurso'} não está disponível no seu plano atual
                            </p>
                        </div>
                    </div>

                    {limitInfo && (
                        <div className="rounded-lg p-3 mt-2" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                            <div className="flex items-center justify-between">
                                <span className="text-xs" style={{ color: 'var(--muted)' }}>Uso atual</span>
                                <span className="text-xs font-bold" style={{ color: target.color }}>{limitInfo.current} / {limitInfo.max}</span>
                            </div>
                            <div className="w-full h-1.5 rounded-full mt-1.5" style={{ background: 'var(--surface-2)' }}>
                                <div className="h-full rounded-full transition-all" style={{ width: `${Math.min((limitInfo.current / limitInfo.max) * 100, 100)}%`, background: target.color }} />
                            </div>
                        </div>
                    )}
                </div>

                {/* Body */}
                <div className="p-6 pt-4 space-y-4">
                    <div className="rounded-xl p-4" style={{ background: target.bg, border: `1px solid ${target.color}20` }}>
                        <div className="flex items-center gap-2 mb-2">
                            <TargetIcon size={16} style={{ color: target.color }} />
                            <span className="text-sm font-bold" style={{ color: target.color }}>Plano {targetName}</span>
                            {price > 0 && <span className="text-sm font-bold ml-auto" style={{ color: 'var(--text)' }}>R$ {price.toFixed(2)}/mês</span>}
                            {price === 0 && upgradePlan === 'enterprise' && <span className="text-xs ml-auto" style={{ color: 'var(--muted)' }}>Sob consulta</span>}
                        </div>
                        <p className="text-xs" style={{ color: 'var(--muted)', lineHeight: 1.6 }}>
                            {upgradePlan === 'lite' && 'Logo da empresa, Ponto Digital inicial, financeiro básico, até 2 colaboradores e muito mais.'}
                            {upgradePlan === 'start' && 'Desbloqueie Ponto Digital funcional, até 3 colaboradores, 1 gestor, logo personalizada e muito mais.'}
                            {upgradePlan === 'gestao' && 'Acesse Performance, Financeiro completo, Escalas, PDF, Radar de Ofertas, até 10 colaboradores e 3 gestores.'}
                            {upgradePlan === 'pro' && 'Controle total com Permissões avançadas, Auditoria completa, Radar premium, 25 colaboradores e 5 gestores.'}
                            {upgradePlan === 'enterprise' && 'Solução sob medida com limites personalizados, suporte dedicado e SLA garantido.'}
                        </p>
                    </div>

                    {/* Action buttons */}
                    <div className="flex gap-2">
                        <button
                            onClick={() => { onClose(); navigate('/sistema/empresa/assinatura'); }}
                            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all"
                            style={{ background: target.color, color: '#0A0A0A' }}
                            data-testid="upgrade-modal-view-plans"
                        >
                            <ArrowUpRight size={14} />
                            Ver Planos
                        </button>
                        <button
                            onClick={onClose}
                            className="px-4 py-3 rounded-xl text-sm"
                            style={{ background: 'var(--surface-2)', color: 'var(--muted)', border: '1px solid var(--border)' }}
                            data-testid="upgrade-modal-later"
                        >
                            Depois
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
